import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Table, Card, Row, Col, Badge, Spinner, Button, Nav, Tab } from "react-bootstrap";
import { FiArrowLeft, FiDroplet, FiChevronLeft, FiChevronRight, FiExternalLink } from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

// Asset Detail Interface
interface AssetDetail {
  asset_type: string;
  asset_code?: string;
  asset_issuer?: string;
  paging_token?: string;
  num_accounts?: number;
  num_claimable_balances?: number;
  num_liquidity_pools?: number;
  num_contracts?: number;
  num_archived_contracts?: number;
  amount?: string;
  accounts?: {
    authorized?: number;
    authorized_to_maintain_liabilities?: number;
    unauthorized?: number;
  };
  claimable_balances_amount?: string;
  liquidity_pools_amount?: string;
  contracts_amount?: string;
  archived_contracts_amount?: string;
  balances?: {
    authorized?: string;
    authorized_to_maintain_liabilities?: string;
    unauthorized?: string;
  };
  flags?: {
    auth_required?: boolean;
    auth_revocable?: boolean;
    auth_immutable?: boolean;
    auth_clawback_enabled?: boolean;
  };
  _links?: {
    toml?: { href?: string };
  };
}



// Liquidity Pool Interface
interface LiquidityPoolReserve {
  asset: string;
  amount: string;
}

interface LiquidityPool {
  id: string;
  paging_token: string;
  fee_bp: number;
  type: string;
  total_trustlines: string;
  total_shares: string;
  reserves: LiquidityPoolReserve[];
  last_modified_ledger: number;
  last_modified_time: string;
  _links: {
    self: { href: string };
    transactions: { href: string; templated: boolean };
    operations: { href: string; templated: boolean };
  };
}

interface LiquidityPoolsApiResponse {
  _links: {
    self: { href: string };
    next?: { href: string };
    prev?: { href: string };
  };
  _embedded: {
    records: LiquidityPool[];
  };
}

const AssetDetail: React.FC = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  
  // Parse asset_code and asset_issuer from assetId
  const parseAssetId = (id: string): { assetCode: string; assetIssuer: string } | null => {
    try {
      if (!id || id.trim() === '') return null;
      const decodedId = decodeURIComponent(id);
      const parts = decodedId.split(':');
      if (parts.length === 2 && parts[0].trim() && parts[1].trim()) {
        return { assetCode: parts[0].trim(), assetIssuer: parts[1].trim() };
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const assetInfo = assetId ? parseAssetId(assetId) : null;
  const assetCode = assetInfo?.assetCode;
  const assetIssuer = assetInfo?.assetIssuer;

  const [assetDetail, setAssetDetail] = useState<AssetDetail | null>(null);
  const [liquidityPoolsData, setLiquidityPoolsData] = useState<LiquidityPoolsApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [poolsLoading, setPoolsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [poolsError, setPoolsError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("pools");
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const { t, language } = useLanguage();

  useEffect(() => {
    if (assetId && !assetInfo) {
      setError('Invalid asset ID format. Expected format: asset_code:asset_issuer');
      setLoading(false);
      return;
    }

    if (assetCode && assetIssuer) {
      setHeading(`${assetCode} ${t('assetDetail.heading')}`);
      setTitle(`${assetCode} Asset Details - PiScan`);
      setDescription(`Detailed information about ${assetCode} asset on Pi Network blockchain including trades and liquidity pools.`);
    }
  }, [assetId, assetInfo, assetCode, assetIssuer, setHeading, setTitle, setDescription, t]);



  const fetchAssetDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://api.testnet.minepi.com/assets?asset_code=${assetCode}&asset_issuer=${assetIssuer}&limit=1`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data._embedded?.records?.length > 0) {
        setAssetDetail(data._embedded.records[0]);
      }
      setLoading(false);
    } catch (err) {
      setError(`Failed to fetch asset details: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  }, [assetCode, assetIssuer]);



  const fetchLiquidityPools = useCallback(async (url?: string) => {
    try {
      setPoolsLoading(true);
      // Use the correct API parameter format for filtering by asset code and issuer
      const apiUrl = url || `https://api.testnet.minepi.com/liquidity_pools?reserves=${assetCode}:${assetIssuer}&limit=200`;
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      setLiquidityPoolsData(data);
      
      setPoolsLoading(false);
      setPoolsError(null);
    } catch (err) {
      console.error('Failed to fetch liquidity pools:', err);
      setPoolsError(`Failed to fetch liquidity pools: ${err instanceof Error ? err.message : String(err)}`);
      setPoolsLoading(false);
    }
  }, [assetCode, assetIssuer]);

  // Separate useEffect to call API functions after they are defined
  useEffect(() => {
    if (assetCode && assetIssuer) {
      fetchAssetDetail();
      fetchLiquidityPools();
    }
  }, [assetCode, assetIssuer, fetchAssetDetail, fetchLiquidityPools]);



  const handleNextPools = () => {
    if (liquidityPoolsData?._links?.next) {
      fetchLiquidityPools(liquidityPoolsData._links.next.href);
    }
  };

  const handlePrevPools = () => {
    if (liquidityPoolsData?._links?.prev) {
      fetchLiquidityPools(liquidityPoolsData._links.prev.href);
    }
  };

  const formatNumber = (num?: string | number): string => {
    if (num === null || num === undefined || num === '') return '0';
    const number = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(number)) return '0';
    return number.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  const safeNumber = (num?: number): number => {
    return num ?? 0;
  };

  const safeString = (str?: string): string => {
    return str ?? '0';
  };

  const getTotalAccounts = (accounts?: { authorized?: number; authorized_to_maintain_liabilities?: number; unauthorized?: number }): number => {
    if (!accounts) return 0;
    const authorized = accounts.authorized ?? 0;
    const maintainLiabilities = accounts.authorized_to_maintain_liabilities ?? 0;
    const unauthorized = accounts.unauthorized ?? 0;
    return authorized + maintainLiabilities + unauthorized;
  };

  const getTotalSupply = (assetDetail: AssetDetail): string => {
    const authorizedBalance = parseFloat(assetDetail.balances?.authorized || '0');
    const liquidityPoolsAmount = parseFloat(assetDetail.liquidity_pools_amount || '0');
    const total = authorizedBalance + liquidityPoolsAmount;
    
    if (total === 0) return "0";
    if (total < 0.0001) return "<0.0001";
    return formatNumber(total.toString());
  };

  const getPoolsAmount = (assetDetail: AssetDetail): string => {
    const liquidityPoolsAmount = parseFloat(assetDetail.liquidity_pools_amount || '0');
    
    if (liquidityPoolsAmount === 0) return "0";
    if (liquidityPoolsAmount < 0.0001) return "<0.0001";
    return formatNumber(liquidityPoolsAmount.toString());
  };

  const formatAsset = (assetType: string, assetCode?: string): string => {
    if (assetType === 'native') return 'PI';
    return assetCode || 'Unknown';
  };

  const formatReserveAsset = (asset: string): string => {
    if (asset === 'native') return 'Pi';
    
    // Check if it contains a colon (asset_code:asset_issuer format)
    if (asset.includes(':')) {
      const parts = asset.split(':');
      if (parts.length === 2) {
        const assetCode = parts[0];
        const assetIssuer = parts[1];
        // Show asset code and shortened issuer
        const shortenedIssuer = `${assetIssuer.slice(0, 4)}...${assetIssuer.slice(-4)}`;
        return `${assetCode}:${shortenedIssuer}`;
      }
    }
    
    return asset;
  };

  const formatDateTime = (dateTime: string): string => {
    const date = new Date(dateTime);
    return date.toLocaleString(language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };



  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  if (!assetDetail) {
    return <div className="alert alert-warning">Asset not found</div>;
  }

  return (
    <div className="asset-detail-page">
      {/* Back Button */}
      <Row className="mb-3">
        <Col>
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => navigate('/assets')}
            className="d-flex align-items-center"
          >
            {FiArrowLeft({size:16,className:'me-2'})}
            {t('assetDetail.back_to_assets')}
          </Button>
        </Col>
      </Row>

      {/* Asset Header */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                
                <div>
                  <h2 className="mb-1">{assetCode}</h2>
                  <p className="text-muted mb-0">{t('assetDetail.asset_info')}</p>
                  <div className="alert alert-warning py-2 px-3 mt-2 mb-0">
                    <small>
                      <strong>{t('assetDetail.testnet_warning')}</strong> {t('assetDetail.testnet_description')}
                    </small>
                  </div>
                  {assetIssuer && (
                    <div className="mt-2">
                      <small className="text-muted me-2">Issuer:</small>
                      <a 
                        href={`/account/${assetIssuer}`}
                        className="text-decoration-none"
                        title={assetIssuer}
                      >
                        <code className="small text-primary">
                          {assetIssuer.slice(0, 12)}...{assetIssuer.slice(-12)}
                        </code>
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <Row>
                <Col lg={2} md={4} className="mb-3">
                  <div className="text-center p-3 border rounded bg-light">
                    <div className="h5 mb-0 text-primary">{getTotalSupply(assetDetail)}</div>
                    <small className="text-muted">{t('assetDetail.total_supply')}</small>
                  </div>
                </Col>
                <Col lg={2} md={4} className="mb-3">
                  <div className="text-center p-3 border rounded bg-light">
                    <div className="h5 mb-0 text-info">{formatNumber(getTotalAccounts(assetDetail.accounts))}</div>
                    <small className="text-muted">{t('assetDetail.holders')}</small>
                  </div>
                </Col>
                <Col lg={2} md={4} className="mb-3">
                  <div className="text-center p-3 border rounded bg-light">
                    <div className="h5 mb-0 text-success">{safeNumber(assetDetail.num_liquidity_pools)}</div>
                    <small className="text-muted">{t('assetDetail.liquidity_pools')}</small>
                  </div>
                </Col>
                <Col lg={2} md={4} className="mb-3">
                  <div className="text-center p-3 border rounded bg-light">
                    <div className="h5 mb-0 text-warning">{safeNumber(assetDetail.num_claimable_balances)}</div>
                    <small className="text-muted">{t('assetDetail.claimable_balances')}</small>
                  </div>
                </Col>
                <Col lg={2} md={4} className="mb-3">
                  <div className="text-center p-3 border rounded bg-light">
                    <div className="h5 mb-0 text-secondary">{getPoolsAmount(assetDetail)}</div>
                    <small className="text-muted">{t('assetDetail.in_pools')}</small>
                  </div>
                </Col>
                <Col lg={2} md={4} className="mb-3">
                  <div className="text-center p-3 border rounded bg-light">
                    <div className="h5 mb-0 text-dark">{formatNumber(assetDetail.balances?.authorized || '0')}</div>
                    <small className="text-muted">{t('assetDetail.circulating')}</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Row>
        <Col>
          <Card>
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || "pools")}>
              <Card.Header className="p-0">
                <Nav variant="tabs" className="border-0">
                  <Nav.Item>
                    <Nav.Link eventKey="pools" className="d-flex align-items-center">
                      {FiDroplet({size:16,className:'me-2'})}
                      {t('assetDetail.liquidity_pools')}
                      {liquidityPoolsData && (
                        <Badge bg="info" className="ms-2">
                          {liquidityPoolsData._embedded?.records?.length || 0}
                        </Badge>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Header>
              
              <Tab.Content>
                {/* Liquidity Pools Tab */}
                <Tab.Pane eventKey="pools">
                  <Card.Body>
                    <Row className="mb-3">
                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          {FiDroplet({size:20,className:'me-2 text-info'})}
                          <h5 className="mb-0">{t('assetDetail.liquidity_pools_list')}</h5>
                        </div>
                      </Col>
                      <Col md={6} className="d-flex justify-content-end">
                        <div className="d-flex gap-2">
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            disabled={!liquidityPoolsData?._links?.prev || poolsLoading}
                            onClick={handlePrevPools}
                          >
                            {FiChevronLeft({size:16,className:'me-1'})}
                            {t('assetDetail.previous')}
                          </Button>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            disabled={!liquidityPoolsData?._links?.next || poolsLoading}
                            onClick={handleNextPools}
                          >
                            {t('assetDetail.next')}
                            {FiChevronRight({size:16,className:'ms-1'})}
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    {poolsLoading ? (
                      <div className="d-flex justify-content-center my-4">
                        <Spinner animation="border" role="status" size="sm">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      </div>
                    ) : poolsError ? (
                      <div className="alert alert-warning my-4">
                        <strong>Unable to load liquidity pools:</strong> {poolsError}
                        <br />
                        <small className="text-muted">
                          This asset may not be available in any liquidity pools, or there might be a temporary API issue.
                        </small>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead>
                            <tr>
                              <th>{t('assetDetail.pool_id')}</th>
                              <th>{t('assetDetail.reserves')}</th>
                              <th className="text-end">{t('assetDetail.total_shares')}</th>
                              <th className="text-end">{t('assetDetail.fee')}</th>
                              <th className="text-end">{t('assetDetail.trustlines')}</th>
                              <th>{t('assetDetail.last_modified')}</th>
                              <th className="text-center">{t('assetDetail.links')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {liquidityPoolsData?._embedded?.records?.length === 0 ? (
                              <tr>
                                <td colSpan={7} className="text-center py-4 text-muted">
                                  <div>
                                    <div className="mb-2">{t('assetDetail.no_pools_found')}</div>
                                    <small>This asset is not currently used in any liquidity pools.</small>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              liquidityPoolsData?._embedded?.records?.map((pool) => (
                              <tr key={pool.id}>
                                <td>
                                  <code className="small">
                                    {pool.id.slice(0, 8)}...{pool.id.slice(-8)}
                                  </code>
                                </td>
                                <td>
                                  <div>
                                    {pool.reserves.map((reserve, index) => (
                                      <div key={index} className="mb-1">
                                        <span className="fw-medium">{formatNumber(reserve.amount)}</span>{' '}
                                        <span className="text-muted">{formatReserveAsset(reserve.asset)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                                <td className="text-end">
                                  <span className="fw-medium">{formatNumber(pool.total_shares)}</span>
                                </td>
                                <td className="text-end">
                                  <Badge bg="info">{(pool.fee_bp / 100).toFixed(2)}%</Badge>
                                </td>
                                <td className="text-end">
                                  <span className="fw-medium">{formatNumber(pool.total_trustlines)}</span>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {formatDateTime(pool.last_modified_time)}
                                  </small>
                                </td>
                                <td className="text-center">
                                  <div className="d-flex flex-column gap-1">
                                    <a 
                                      href={`/pool/${pool.id}/history`}
                                      className="text-primary text-decoration-none small"
                                      title={t('assetDetail.view_operations')}
                                    >
                                      {t('assetDetail.history')}
                                    </a>
                                    
                                  </div>
                                </td>
                              </tr>
                            )))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AssetDetail;
